import {
  UnstyledButton,
  type UnstyledButtonProps,
  Flex,
  Collapse,
} from '@mantine/core';
import {
  type FC,
  type PropsWithChildren,
  type ReactNode,
  useState,
} from 'react';
import classes from './navbarButton.module.scss';
import { NavLink, useLocation } from 'react-router';

const getIsActiveLink = ({
  pathname,
  to,
  activeStartWith,
  isMainPage,
}: {
  pathname: string;
  to: string;
  activeStartWith?: string;
  isMainPage?: boolean;
}) => {
  if (isMainPage) {
    return pathname === '/' || pathname.startsWith(activeStartWith ?? '');
  }

  if (activeStartWith) {
    return pathname.startsWith(activeStartWith);
  }

  return pathname.startsWith(to);
};

interface MenuItem {
  to: string;
  label: string;
  icon?: ReactNode;
  active?: boolean;
}

interface NavbarButtonProps extends UnstyledButtonProps, PropsWithChildren {
  to: string;
  icon: ReactNode;
  isMainPage?: boolean;
  activeStartWith?: string;
  menuItems?: MenuItem[];
  onNavClick?: () => void;
}

export const NavbarButton: FC<NavbarButtonProps> = ({
  to,
  children,
  icon,
  menuItems,
  isMainPage,
  activeStartWith,
  onNavClick,
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const location = useLocation();

  const isActive = getIsActiveLink({
    pathname: location.pathname,
    to,
    activeStartWith,
    isMainPage,
  });

  const handleNavClick = () => {
    onNavClick?.();
  };

  if (!menuItems) {
    return (
      <UnstyledButton
        {...props}
        to={to ?? '/'}
        component={NavLink}
        className={`${classes.root} ${isActive ? classes.menuItemActive : ''}`}
        onClick={handleNavClick}
      >
        {icon && icon}
        {children}
      </UnstyledButton>
    );
  }

  return (
    <>
      <UnstyledButton
        {...props}
        className={`${classes.root} ${isActive ? classes.menuItemActive : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        {icon && icon}
        {children}
      </UnstyledButton>

      <Collapse in={isOpen}>
        <Flex direction='column' gap={4} className={classes.menuItems}>
          {menuItems.map(({ to, label, icon }) => (
            <UnstyledButton
              {...props}
              key={to}
              to={to}
              component={NavLink}
              className={classes.menuItem}
              onClick={handleNavClick}
            >
              {icon && icon}
              {label}
            </UnstyledButton>
          ))}
        </Flex>
      </Collapse>
    </>
  );
};
